package dev.lingvo.site.components.studio.fonts

import dev.lingvo.shared.screenshots.text.FontCatalog
import dev.lingvo.shared.screenshots.text.FontStack
import dev.lingvo.shared.screenshots.text.cssFont
import kotlinx.browser.document
import kotlinx.coroutines.await
import kotlinx.coroutines.CancellationException
import dev.lingvo.shared.screenshots.text.Scripts
import kotlin.js.Promise

/** Prevents export from measuring fallback glyphs while real caption subsets are still loading. */
object FontGate {
    private val loader = FontLoader { request, attempt ->
        if (!FontLinks.ready(request.family, retry = attempt > 1)) false
        else {
            val fonts = document.asDynamic().fonts
            if (fonts == null) false else {
                val css = cssFont(FontStack(listOf(request.family), "sans-serif"), 100.0, request.weight, request.italic)
                try { fonts.load(css, request.sample).unsafeCast<Promise<Array<Any>>>().await().isNotEmpty() }
                catch (cancelled: CancellationException) { throw cancelled }
                catch (_: Throwable) { FontLinks.invalidate(request.family); false }
            }
        }
    }
    private val loading = FontLoading(loader, cachedReady = { request ->
        val fonts = document.asDynamic().fonts
        FontLinks.loaded(request.family) && fonts != null && fonts.check(
            cssFont(FontStack(listOf(request.family), "sans-serif"), 100.0, request.weight, request.italic), request.sample,
        ) == true
    })

    suspend fun load(requests: List<FontLoadRequest>, progress: (FontLoadProgress) -> Unit = {}): FontLoadRequest? =
        loading.load(requests, progress)

    /** Kept for preview callers; compose uses load to expose the exact family and progress. */
    suspend fun ensure(stack: FontStack, weight: Int, italic: Boolean, sample: String, timeoutMs: Int = 60_000): Boolean {
        if (timeoutMs <= 0 || stack.families.isEmpty() || stack.families.any { FontCatalog.byName(it) == null }) return false
        // A fresh owner keeps explicit short deadlines useful for previews and browser fixtures.
        val gate = if (timeoutMs == 60_000) loading else FontLoading(loader, FontWaitPolicy(timeoutMs.toLong()))
        val script = Scripts.scriptFromText(sample)
        return gate.load(stack.families.map { family ->
            val coveredSample = if (script in FontCatalog.byName(family)?.scripts.orEmpty() || family == "Noto Color Emoji") sample else "Lingvo $sample"
            FontLoadRequest(family, weight, italic, coveredSample)
        }) == null
    }
}
